import React, { memo } from 'react';
import { Toast } from '../../../../hooks/useToast';
import { BillingStatistics } from '../../../../types/portvilleBilling';

interface AlertProps {
  children: React.ReactNode;
  type: 'error' | 'success' | 'info' | 'warning';
}

const Alert = memo<AlertProps>(({ children, type }) => {
  const baseClasses = "mb-4 p-4 border rounded-md";
  const typeClasses = {
    error: "bg-red-50 border-red-200",
    success: "bg-green-50 border-green-200",
    info: "bg-blue-50 border-blue-200",
    warning: "bg-yellow-50 border-yellow-200"
  };

  const textClasses = {
    error: "text-red-800",
    success: "text-green-800",
    info: "text-blue-800",
    warning: "text-yellow-800"
  };

  return (
    <div className={`${baseClasses} ${typeClasses[type]}`}>
      <p className={textClasses[type]}>{children}</p>
    </div>
  );
});

interface ConnectionStatusProps {
  connected: boolean;
}

const ConnectionStatus = memo<ConnectionStatusProps>(({ connected }) => {
  if (connected) return null;

  return (
    <Alert type="error">
      서버 연결이 끊어졌습니다. 네트워크 상태를 확인해 주세요.
    </Alert>
  );
});

interface ErrorDisplayProps {
  error: string | null;
}

const ErrorDisplay = memo<ErrorDisplayProps>(({ error }) => {
  if (!error) return null;

  return <Alert type="error">{error}</Alert>;
});

interface ToastContainerProps {
  toasts: Toast[];
  onHideToast: (id: string) => void;
}

const ToastContainer = memo<ToastContainerProps>(({ toasts, onHideToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="space-y-2">
      {toasts.map((toast) => (
        <Alert key={toast.id} type={toast.type}>
          <div className="flex justify-between items-center">
            <span>{toast.message}</span>
            <button
              onClick={() => onHideToast(toast.id)}
              className="ml-4 text-sm underline hover:no-underline"
            >
              닫기
            </button>
          </div>
        </Alert>
      ))}
    </div>
  );
});

interface BillingStatisticsDisplayProps {
  billingStatistics: BillingStatistics | null;
}

const BillingStatisticsDisplay = memo<BillingStatisticsDisplayProps>(({ billingStatistics }) => {
  if (!billingStatistics) return null;

  return (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
      <h3 className="font-medium text-blue-900 mb-2">통계 정보</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-blue-700">수납률:</span>
          <span className="ml-2 font-medium">{billingStatistics.collectionRate.toFixed(1)}%</span>
        </div>
        <div>
          <span className="text-blue-700">총 청구액:</span>
          <span className="ml-2 font-medium">{billingStatistics.totalBilledAmount.toLocaleString()}원</span>
        </div>
        <div>
          <span className="text-blue-700">총 납부액:</span>
          <span className="ml-2 font-medium">{billingStatistics.totalPaidAmount.toLocaleString()}원</span>
        </div>
        <div>
          <span className="text-blue-700">연체액:</span>
          <span className="ml-2 font-medium text-red-600">{billingStatistics.totalOverdueAmount.toLocaleString()}원</span>
        </div>
      </div>
    </div>
  );
});

interface BillingAlertsProps {
  connected: boolean;
  error: string | null;
  toasts: Toast[];
  onHideToast: (id: string) => void;
}

const BillingAlerts = memo<BillingAlertsProps>(({
  connected,
  error,
  toasts,
  onHideToast
}) => (
  <>
    <ConnectionStatus connected={connected} />
    <ErrorDisplay error={error} />
    <ToastContainer toasts={toasts} onHideToast={onHideToast} />
  </>
));

Alert.displayName = 'Alert';
ConnectionStatus.displayName = 'ConnectionStatus';
ErrorDisplay.displayName = 'ErrorDisplay';
ToastContainer.displayName = 'ToastContainer';
BillingStatisticsDisplay.displayName = 'BillingStatisticsDisplay';
BillingAlerts.displayName = 'BillingAlerts';

export default BillingAlerts;